import type { BriefSection } from '../types/brief'

export const briefTitle = 'Meet the Filtek’s'

export const briefSubtitle =
  'Turning a product family into a family people can actually understand'

export const briefSections: BriefSection[] = [
  {
    id: 'challenge',
    nav: 'The challenge',
    title: 'The challenge',
    blocks: [
      {
        kind: 'p',
        text: 'Filtek has a strong portfolio, but the idea of the “Filtek Family” is harder to communicate than it sounds.',
      },
      {
        kind: 'p',
        text: 'Professionally, the logic makes sense: different restorative situations require different strengths. One solution may be best when aesthetic control matters, another when simplicity matters, another when efficiency matters.',
      },
      { kind: 'p', text: 'But to a clinician, this can easily look like:' },
      { kind: 'note', text: 'more products, more choices, more complexity.' },
      {
        kind: 'p',
        text: 'That challenge has already surfaced in discussions around the Filtek range. One clinician described the portfolio as difficult to understand until it was simplified around clear jobs such as high aesthetics, everyday use and posterior restorations. Once the portfolio logic became clear, her perception changed: she realised she did not necessarily need to leave Filtek and go to a competitor for other cases.',
      },
      { kind: 'p', text: 'That is the real opportunity.' },
      {
        kind: 'p',
        text: 'We do not simply need to explain what the products are.',
      },
      { kind: 'p', text: 'We need people to intuitively understand:' },
      { kind: 'statement', text: 'Why are they a family?' },
    ],
  },
  {
    id: 'strategic-leap',
    nav: 'The strategic leap',
    title: 'The strategic leap',
    blocks: [
      {
        kind: 'p',
        text: 'Instead of using “family” as a corporate portfolio term, we make it behave like a real family.',
      },
      { kind: 'p', text: 'Because real families are easy to understand.' },
      { kind: 'lines', lines: ['They share something.', 'But they are not identical.'] },
      {
        kind: 'p',
        text: 'They have different personalities, different talents, different roles and different ways of approaching things.',
      },
      {
        kind: 'lines',
        lines: [
          'One sibling may be meticulous.',
          'Another adaptable.',
          'Another impatient and efficient.',
        ],
      },
      {
        kind: 'p',
        text: 'Then there is the unexpected youngest — the caboose child — who arrives later and changes the family dynamic.',
      },
      { kind: 'p', text: 'Suddenly the Filtek portfolio becomes far easier to grasp.' },
      {
        kind: 'statement',
        text: 'Same family. Different personalities. Different strengths.',
      },
      {
        kind: 'p',
        text: 'This is not simply a creative wrapper around product information. It is a translation device.',
      },
      {
        kind: 'p',
        text: 'It converts a technically complex product architecture into a human idea that can be understood almost instantly.',
      },
    ],
  },
  {
    id: 'logos-to-people',
    nav: 'From logos to people',
    title: 'From logos to people',
    blocks: [
      { kind: 'p', text: 'There is another important benefit.' },
      {
        kind: 'p',
        text: 'Medical and dental communication is overwhelmingly rational.',
      },
      { kind: 'lines', lines: ['Claims. Products. Techniques. Evidence. Logos.'] },
      {
        kind: 'p',
        text: 'That creates logos — reason and proof — but relatively little pathos.',
      },
      { kind: 'p', text: '“Meet the Filtek’s” introduces warmth.' },
      {
        kind: 'p',
        text: 'Before we talk about our family, we ask about your family.',
      },
      { kind: 'note', text: 'What are you?' },
      {
        kind: 'lines',
        lines: ['Firstborn?', 'Middle child?', 'Youngest?', 'Only child?'],
      },
      {
        kind: 'p',
        text: 'Almost everyone immediately has a response. They know what they are. They probably have an opinion about what it means. They may disagree completely with the stereotype — which is part of the fun.',
      },
      {
        kind: 'p',
        text: 'For a moment, Solventum is not talking to a dentist about dentistry.',
      },
      { kind: 'note', text: 'It is talking to a person.' },
      { kind: 'p', text: 'That matters.' },
      {
        kind: 'p',
        text: 'The people behind Solventum have families too. Personalities. Siblings. Inside jokes. Values. Relationships.',
      },
      {
        kind: 'p',
        text: 'The brand becomes a little less institutional and a little more human. Not less professional. More relatable.',
      },
    ],
  },
  {
    id: 'cartoon-style',
    nav: 'Why the cartoon style matters',
    title: 'Why the cartoon style matters',
    visual: 'characters',
    blocks: [
      {
        kind: 'p',
        text: 'The slightly cartoonish visual language is therefore not decoration. It gives the audience permission to play.',
      },
      {
        kind: 'p',
        text: 'The corporate Solventum design system establishes credibility and recognition. Inside that world, the illustrated sibling characters introduce surprise, humour and warmth.',
      },
      { kind: 'p', text: 'The tension is deliberate:' },
      {
        kind: 'lines',
        lines: ['Serious brand.', 'Serious products.', 'Unexpectedly playful conversation.'],
      },
      { kind: 'p', text: 'That contrast is what makes it noticeable.' },
      {
        kind: 'p',
        text: 'If we illustrated everything clinically, the experience would simply become another product communication. If we made everything cartoonish, we would weaken credibility.',
      },
      { kind: 'p', text: 'The sweet spot is:' },
      { kind: 'statement', text: 'Solventum on the outside. Personality on the inside.' },
    ],
  },
  {
    id: 'gamification',
    nav: 'Gamification with a purpose',
    title: 'Gamification with a purpose',
    blocks: [
      {
        kind: 'p',
        text: 'The gamification is also doing more than generating clicks.',
      },
      {
        kind: 'p',
        text: 'We start with a question the audience already knows how to answer:',
      },
      { kind: 'note', text: 'Where do you sit in your family?' },
      {
        kind: 'p',
        text: 'That creates the first small commitment. Then we give them a playful interpretation:',
      },
      { kind: 'quote', text: 'Ah, the middle child. Some say…' },
      {
        kind: 'p',
        text: 'Whether they agree or disagree does not really matter. They are participating.',
      },
      { kind: 'p', text: 'Then we turn the same game onto the Filtek portfolio.' },
      { kind: 'note', text: 'Meet Filtek Supreme.' },
      { kind: 'p', text: 'Precise. Detail-focused. Likes things done properly.' },
      { kind: 'note', text: 'What sibling does that sound like?' },
      {
        kind: 'p',
        text: 'Now the audience is not being told the positioning. They are working it out themselves. That is a fundamentally different cognitive experience.',
      },
      { kind: 'p', text: 'Instead of “Supreme = aesthetic control.” we create:' },
      {
        kind: 'quote',
        text: 'This sounds like the perfectionist of the family… probably the firstborn.',
      },
      {
        kind: 'p',
        text: 'The interaction makes the product characteristic memorable. The audience has helped create the association.',
      },
    ],
  },
  {
    id: 'the-reveal',
    nav: 'The reveal',
    title: 'The reveal',
    visual: 'lineup',
    blocks: [
      {
        kind: 'p',
        text: 'Then comes the important strategic landing. The joke is not that Filtek products are literally siblings. The point is:',
      },
      { kind: 'statement', text: 'There is no best sibling.' },
      {
        kind: 'p',
        text: 'They are simply good at different things. And restorative cases are the same.',
      },
      {
        kind: 'lines',
        lines: [
          'One case asks for aesthetic control.',
          'Another asks for simplicity.',
          'Another asks for efficiency.',
          'Another benefits from changing how the material behaves.',
        ],
      },
      { kind: 'p', text: 'That finally gives meaning to the term:' },
      { kind: 'statement', text: 'Filtek Family.' },
      {
        kind: 'p',
        text: 'A family is useful precisely because its members are not identical.',
      },
    ],
  },
  {
    id: 'the-warmer',
    nav: 'The Warmer',
    title: 'The Warmer makes the metaphor richer',
    blocks: [
      {
        kind: 'p',
        text: 'The Filtek Composite Warmer gives us an especially nice extension of the idea. It does not need to be forced into exactly the same role as the composites. Instead:',
      },
      { kind: 'note', text: 'And just when you thought you had met everyone…' },
      { kind: 'statement', text: 'Meet the caboose child.' },
      {
        kind: 'lines',
        lines: [
          'The late arrival.',
          'A little different from everyone else.',
          'And somehow capable of changing the family dynamic.',
        ],
      },
      {
        kind: 'p',
        text: 'That creates a second surprise and prevents the format from becoming repetitive. The Warmer becomes a character with a genuinely different role in the story rather than “product number four”.',
      },
    ],
  },
  {
    id: 'takeaway',
    nav: 'The takeaway',
    title: 'What the audience should leave understanding',
    blocks: [
      {
        kind: 'p',
        text: 'The desired takeaway is not “Solventum has several Filtek products.” They probably knew that. It is:',
      },
      { kind: 'note', text: '“Now I understand why they belong together.”' },
      { kind: 'p', text: 'And ultimately:' },
      { kind: 'statement', text: 'Different jobs. Different strengths. Same family.' },
      { kind: 'p', text: 'Or in the broader Filtek Family communication:' },
      { kind: 'statement', text: 'Change the solution. Not the system.' },
    ],
  },
  {
    id: 'brand-platform',
    nav: 'Bigger than one Snak',
    title: 'Why this can become bigger than one Snak',
    blocks: [
      {
        kind: 'p',
        text: '“Meet the Filtek’s” could become a small brand platform rather than merely one teaser. Once the audience knows the family, the characters can reappear.',
      },
      {
        kind: 'lines',
        lines: [
          'At congresses.',
          'In social.',
          'In sales-rep conversations.',
          'In product launches.',
          'In “new family member” introductions.',
          'In technique education.',
          'In the larger Filtek Family website.',
        ],
      },
      {
        kind: 'p',
        text: 'The personalities give Solventum something product architecture rarely provides:',
      },
      { kind: 'note', text: 'memory structures.' },
      {
        kind: 'p',
        text: 'People may forget a comparison table. They are much more likely to remember the perfectionist, the adaptable one, the efficient one and the caboose child.',
      },
      {
        kind: 'p',
        text: 'And beneath the humour sits exactly the commercial message Filtek needs to communicate:',
      },
    ],
  },
]

export const briefClosing = {
  statement: 'You do not need every case to behave the same way.',
  support: 'That is why you have a family.',
}
